import { Component } from '@theme/component';
import { isClickedOutside, normalizeString } from '@theme/utilities';

/**
 * One dropdown of the header's split language/country switcher
 * (snippets/mx-localization-switcher.liquid). The header renders two
 * independent instances side by side — language and country — each wrapping
 * its own trigger button, panel and `{% form 'localization' %}`, so this
 * class carries both jobs and the markup decides which parts exist: the
 * country instance has a search field, the language one doesn't.
 *
 * Modelled on Horizon's DropdownLocalizationComponent (hidden attribute +
 * aria-expanded + outside click + Escape) rather than subclassing it: that
 * component is welded to the combined localization-form snippet's refs, and
 * this one submits from list buttons instead of a native select.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} button - The trigger.
 * @property {HTMLElement} panel - The dropdown panel.
 * @property {HTMLFormElement} form - The localization form inside the panel.
 * @property {HTMLInputElement} valueInput - Hidden language_code/country_code input.
 * @property {HTMLInputElement} [search] - Country filter input (country instance only).
 * @property {HTMLButtonElement} [resetButton] - Clears the filter (country instance only).
 * @property {HTMLElement} [noResultsMessage] - Empty-filter message (country instance only).
 * @property {HTMLElement} [liveRegion] - Announces the result count (country instance only).
 * @property {HTMLButtonElement[]} [optionItems] - One button per selectable option.
 *
 * @extends {Component<Refs>}
 */
class MxLocalizationDropdown extends Component {
  requiredRefs = ['button', 'panel', 'form', 'valueInput'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.#onDocumentClick);
    this.addEventListener('keyup', this.#onKeyUp);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick);
    this.removeEventListener('keyup', this.#onKeyUp);
    super.disconnectedCallback();
  }

  get isHidden() {
    return this.refs.panel.hasAttribute('hidden');
  }

  toggleSelector() {
    if (this.isHidden) {
      this.showPanel();
    } else {
      this.hidePanel();
    }
  }

  showPanel() {
    this.refs.panel.removeAttribute('hidden');
    this.refs.button.setAttribute('aria-expanded', 'true');

    const focusTarget = this.refs.search ?? this.querySelector('button[data-value]');
    if (focusTarget instanceof HTMLElement) focusTarget.focus();
  }

  hidePanel() {
    if (this.isHidden) return;

    this.refs.panel.setAttribute('hidden', '');
    this.refs.button.setAttribute('aria-expanded', 'false');
  }

  /**
   * Submits the clicked option's value through the hidden input, the same
   * POST to /localization Horizon's combined form makes.
   *
   * @param {Event & { target: HTMLElement }} event - Click on an option button.
   */
  selectOption(event) {
    const option = event.target.closest('[data-value]');
    if (!(option instanceof HTMLElement) || !option.dataset.value) return;

    this.refs.valueInput.value = option.dataset.value;
    this.#cleanReturnTo();
    this.refs.form.submit();
  }

  /**
   * Rebuilds the form's return_to from the page the visitor is actually on.
   *
   * `{% form 'localization' %}` fills return_to in from the URL of the request
   * that rendered it, which is not always the URL the visitor is looking at.
   * The header re-renders itself through the Section Rendering API:
   * sections/header.liquid calls hydrate(), which fetches
   * `?section_id=sections--…__header_section` and swaps in every node carrying
   * data-hydration-key. One of those two nodes is the mobile drawer, so the
   * drawer's copy of this form came back carrying that fetch's URL —
   * `return_to=/?section_id=sections--…__header_section` — while the desktop
   * header's copy, outside any hydration key, kept the real one.
   *
   * Shopify honours return_to, so switching language or currency in the drawer
   * landed the visitor on a bare section fragment: no layout, no importmap, a
   * blank title and seven "Failed to resolve module specifier '@theme/…'"
   * errors. Desktop was unaffected, which is what made it look like a
   * mobile-only fault rather than a stale return_to.
   *
   * Taking the value from location rather than editing what the markup carries
   * fixes a second, quieter symptom of the same cause: the hydration fetch's URL
   * dropped the page parameter too, so switching language from the drawer on
   * page 2 of a search returned to page 1. Measured before this change — the
   * desktop form held `/search?q=cider&page=2` and the drawer's held
   * `/search?q=cider&section_id=…`. location is the one source that is right by
   * definition at the moment of submit, whoever rendered the markup and
   * whenever.
   *
   * The section-rendering parameters are still stripped, for the case where the
   * visitor is already standing on such a URL — a bookmark, or a reload after
   * hitting the old fault.
   *
   * One difference a reviewer measuring locally will see: on the dev server
   * Shopify's default return_to carries `_fd=0&pb=0`, which is not in the
   * address bar, so rebuilding from location drops them and the desktop value
   * goes from `/?_fd=0&pb=0` to `/`. Those are the dev server's own preview
   * parameters — checked against the live store, where the default return_to is
   * already a bare `/` — so on a real storefront this produces the same value
   * Shopify would have. Anything genuinely in the address bar, preview_theme_id
   * included, is kept.
   *
   * Done in JS rather than Liquid because Liquid cannot read the query string:
   * the options there are request.path or canonical_url, and both would throw
   * away every legitimate parameter on every switch, desktop included.
   *
   * Not specific to this component either — any `{% form 'localization' %}`
   * inside a hydrated node inherits the same wrong default.
   */
  #cleanReturnTo() {
    const input = this.refs.form.querySelector('input[name="return_to"]');
    if (!(input instanceof HTMLInputElement)) return;

    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.delete('sections');
    input.value = `${url.pathname}${url.search}${url.hash}`;
  }

  /**
   * Hides options whose data-filter doesn't contain the query. Diacritics
   * are stripped on both sides (normalizeString), so "aland" finds "Ålandy".
   */
  filterOptions() {
    const { search, resetButton, noResultsMessage, liveRegion, optionItems = [] } = this.refs;
    if (!(search instanceof HTMLInputElement)) return;

    const query = normalizeString(search.value.trim());
    resetButton?.classList.toggle('hidden', query.length === 0);

    let visibleCount = 0;
    for (const option of optionItems) {
      const matches = query.length === 0 || normalizeString(option.dataset.filter ?? '').includes(query);
      option.parentElement?.toggleAttribute('hidden', !matches);
      if (matches) visibleCount += 1;
    }

    noResultsMessage?.toggleAttribute('hidden', visibleCount > 0);

    if (liveRegion) {
      liveRegion.textContent = (this.dataset.labelResultsCount ?? '').replace('[count]', String(visibleCount));
    }
  }

  resetFilter() {
    const { search } = this.refs;
    if (!(search instanceof HTMLInputElement)) return;

    search.value = '';
    this.filterOptions();
    search.focus();
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (this.isHidden) return;
    if (isClickedOutside(event, this)) this.hidePanel();
  };

  /** @param {KeyboardEvent} event */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.hidePanel();
    this.refs.button.focus();
  };
}

if (!customElements.get('mx-localization-dropdown')) {
  customElements.define('mx-localization-dropdown', MxLocalizationDropdown);
}
